import React from "react";
import type { DocumentChangeReportSummaryEntity } from "../../../domain/entities/DocumentChangeAuditEntity";
import {
  getBackendName,
  getStoredAccountingBackend,
} from "../../../../../config/accounting";

interface DocumentChangeSummaryCardsProps {
  summary: DocumentChangeReportSummaryEntity | null;
  entityTypeFilter: "" | "bill" | "invoice";
}

const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  subtitle: string;
  accentClass?: string;
}> = ({ title, value, subtitle, accentClass = "bg-default-background" }) => (
  <div
    className={`flex min-w-[180px] flex-1 flex-col items-start gap-2 rounded-lg border border-solid border-neutral-border p-5 ${accentClass}`}
  >
    <span className="text-caption font-caption text-subtext-color">{title}</span>
    <span className="text-heading-2 font-heading-2 text-default-font">
      {value}
    </span>
    <span className="text-caption font-caption text-subtext-color">
      {subtitle}
    </span>
  </div>
);

export const DocumentChangeSummaryCards: React.FC<
  DocumentChangeSummaryCardsProps
> = ({ summary, entityTypeFilter }) => {
  if (!summary) {
    return (
      <div className="flex w-full flex-wrap gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="flex min-w-[180px] flex-1 flex-col gap-2 rounded-lg border border-solid border-neutral-border bg-neutral-50 p-5"
          >
            <div className="h-4 w-24 rounded bg-neutral-200" />
            <div className="h-8 w-16 rounded bg-neutral-200" />
            <div className="h-3 w-32 rounded bg-neutral-200" />
          </div>
        ))}
      </div>
    );
  }

  const autoPostedRate = (posted: number, withoutChanges: number): string =>
    posted > 0 ? ((withoutChanges / posted) * 100).toFixed(1) : "0.0";

  const totals =
    entityTypeFilter === "bill"
      ? {
          posted: summary.byEntityType.bill.posted,
          withChanges: summary.byEntityType.bill.withChanges,
          withoutChanges: summary.byEntityType.bill.withoutChanges,
          autoPostedRate: autoPostedRate(
            summary.byEntityType.bill.posted,
            summary.byEntityType.bill.withoutChanges,
          ),
          splitLabel: "Posted bills only",
        }
      : entityTypeFilter === "invoice"
        ? {
            posted: summary.byEntityType.invoice.posted,
            withChanges: summary.byEntityType.invoice.withChanges,
            withoutChanges: summary.byEntityType.invoice.withoutChanges,
            autoPostedRate: autoPostedRate(
              summary.byEntityType.invoice.posted,
              summary.byEntityType.invoice.withoutChanges,
            ),
            splitLabel: "Posted invoices only",
          }
        : {
            posted: summary.totals.allPosted,
            withChanges: summary.totals.withChanges,
            withoutChanges: summary.totals.withoutChanges,
            autoPostedRate: autoPostedRate(
              summary.totals.allPosted,
              summary.totals.withoutChanges,
            ),
            splitLabel: `${summary.totals.postedBills} bills · ${summary.totals.postedInvoices} invoices`,
          };

  return (
    <div className="flex w-full flex-col items-start gap-3">
      <div className="flex w-full flex-wrap gap-4">
        <SummaryCard
          title="Total Posted"
          value={totals.posted}
          subtitle={totals.splitLabel}
        />
        <SummaryCard
          title="With Changes"
          value={totals.withChanges}
          subtitle="Required manual correction before posting"
          accentClass="bg-amber-50"
        />
        <SummaryCard
          title="Without Changes"
          value={totals.withoutChanges}
          subtitle="Posted as extracted from document"
          accentClass="bg-green-50"
        />
        <SummaryCard
          title="Auto Posted Rate"
          value={`${totals.autoPostedRate}%`}
          subtitle="Share of posted documents that needed no edits"
          accentClass="bg-blue-50"
        />
      </div>
      <span className="text-caption font-caption text-subtext-color">
        Based on documents posted to {getBackendName(getStoredAccountingBackend())}.
        Change details are available only where an approval audit was recorded.
      </span>
    </div>
  );
};
